import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Layout from '../../components/common/Layout';
import { bookService } from '../../services/bookService';
import { reviewService } from '../../services/reviewService';
import { Book, Review } from '../../types/book';
import { useAuth } from '../../context/AuthContext';
import ReviewForm from '../../components/reviews/ReviewForm';
import ReviewList from '../../components/reviews/ReviewList';
import {
  BookOpenIcon,
  StarIcon,
  PencilIcon,
  TrashIcon,
  ArrowLeftIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  TagIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';

const BookDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [book, setBook] = useState<Book | null>(null);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [userReview, setUserReview] = useState<Review | null>(null);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [error, setError] = useState<string>('');

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (id) {
      fetchBook(id);
      fetchReviews(id);
    }
  }, [id]);

  const fetchBook = async (bookId: string) => {
    try {
      setLoading(true);
      const bookData = await bookService.getBookById(bookId);
      if (bookData) {
        setBook(bookData);
      } else {
        setError('Book not found');
      }
    } catch (error) {
      console.error('Error fetching book:', error);
      setError('Failed to load book');
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async (bookId: string) => {
    try {
      setReviewsLoading(true);
      const reviewsData = await reviewService.getReviewsByBookId(bookId);
      setReviews(reviewsData);
      
      // Check if current user has already reviewed this book
      if (user) {
        const existingReview = await reviewService.hasUserReviewed(bookId, user.id);
        setUserReview(existingReview);
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setReviewsLoading(false);
    }
  };

  const handleReviewUpdated = async () => {
    if (id) {
      await fetchReviews(id);
      await fetchBook(id); // Refresh book to update average rating
    }
    setShowReviewForm(false);
  };

  const handleWriteReview = () => {
    setShowReviewForm(true);
  };

  const handleCancelReview = () => {
    setShowReviewForm(false);
  };

  const handleDeleteBook = async () => {
    if (!book || !isAdmin) return;

    if (window.confirm('Are you sure you want to delete this book?')) {
      try {
        await bookService.deleteBook(book.id);
        navigate('/books');
      } catch (error) {
        console.error('Error deleting book:', error);
        alert('Failed to delete book. Please try again.');
      }
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available':
        return 'bg-green-100 text-green-800';
      case 'borrowed':
        return 'bg-yellow-100 text-yellow-800';
      case 'lost':
        return 'bg-red-100 text-red-800';
      case 'donated':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const renderStars = (rating: number) => {
    const stars: JSX.Element[] = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <div key={i} className="relative">
          {i <= rating ? (
            <StarIconSolid className="h-5 w-5 text-yellow-400" />
          ) : (
            <StarIcon className="h-5 w-5 text-gray-300" />
          )}
        </div>
      );
    }
    return stars;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </Layout>
    );
  }

  if (error || !book) {
    return (
      <Layout>
        <div className="text-center py-12">
          <BookOpenIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">{error || 'Book not found'}</h3>
          <div className="mt-6">
            <Link
              to="/books"
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
            >
              <ArrowLeftIcon className="-ml-1 mr-2 h-5 w-5" />
              Back to Books
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        {/* Header with back button and actions */}
        <div className="mb-6 flex items-center justify-between">
          <Link
            to="/books"
            className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-1" />
            Back to Books
          </Link>
          
          {isAdmin && (
            <div className="flex space-x-3">
              <Link
                to={`/edit-book/${book.id}`}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <PencilIcon className="-ml-1 mr-2 h-5 w-5" />
                Edit
              </Link>
              <button
                onClick={handleDeleteBook}
                className="inline-flex items-center px-4 py-2 border border-red-300 shadow-sm text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50"
              >
                <TrashIcon className="-ml-1 mr-2 h-5 w-5" />
                Delete
              </button>
            </div>
          )}
        </div>

        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="p-6 sm:p-8">
            <div className="lg:flex lg:space-x-8">
              {/* Book Cover */}
              <div className="lg:w-1/3">
                {book.coverImageUrl ? (
                  <img
                    src={book.coverImageUrl}
                    alt={book.title}
                    className="w-full max-w-sm mx-auto lg:mx-0 rounded-lg shadow-md"
                  />
                ) : (
                  <div className="w-full max-w-sm mx-auto lg:mx-0 aspect-[3/4] bg-gray-200 rounded-lg shadow-md flex items-center justify-center">
                    <BookOpenIcon className="h-16 w-16 text-gray-400" />
                  </div>
                )}
              </div>

              {/* Book Details */}
              <div className="mt-6 lg:mt-0 lg:w-2/3">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">{book.title}</h1>
                  <p className="text-xl text-gray-600 mt-2">by {book.author}</p>
                  
                  {/* Status Badge */}
                  <div className="mt-4">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(book.status)}`}>
                      {book.status.charAt(0).toUpperCase() + book.status.slice(1)}
                    </span>
                  </div>

                  {/* Rating */}
                  {book.averageRating > 0 && (
                    <div className="mt-4 flex items-center space-x-2">
                      <div className="flex">
                        {renderStars(Math.round(book.averageRating))}
                      </div>
                      <span className="text-sm text-gray-600">
                        {book.averageRating.toFixed(1)} out of 5
                      </span>
                    </div>
                  )}

                  {/* Book Metadata */}
                  <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="flex items-center text-sm text-gray-600">
                      <TagIcon className="h-5 w-5 mr-2 text-gray-400" />
                      <span>Genre: {book.genre}</span>
                    </div>
                    
                    <div className="flex items-center text-sm text-gray-600">
                      <CurrencyDollarIcon className="h-5 w-5 mr-2 text-gray-400" />
                      <span>Price: ${book.acquisitionPrice.toFixed(2)}</span>
                    </div>
                    
                    <div className="flex items-center text-sm text-gray-600">
                      <UserIcon className="h-5 w-5 mr-2 text-gray-400" />
                      <span>Purchased by: {book.purchasedBy}</span>
                    </div>
                    
                    <div className="flex items-center text-sm text-gray-600">
                      <TagIcon className="h-5 w-5 mr-2 text-gray-400" />
                      <span>From: {book.acquiredFrom}</span>
                    </div>
                    
                    <div className="flex items-center text-sm text-gray-600">
                      <CalendarIcon className="h-5 w-5 mr-2 text-gray-400" />
                      <span>Added: {book.createdAt.toLocaleDateString()}</span>
                    </div>
                    
                    {book.updatedAt.getTime() !== book.createdAt.getTime() && (
                      <div className="flex items-center text-sm text-gray-600">
                        <CalendarIcon className="h-5 w-5 mr-2 text-gray-400" />
                        <span>Updated: {book.updatedAt.toLocaleDateString()}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="mt-8">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Description</h3>
              <div className="prose max-w-none">
                <p className="text-gray-700 whitespace-pre-wrap">{book.description}</p>
              </div>
            </div>

            {/* Reviews Section */}
            <div className="mt-8 border-t border-gray-200 pt-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-medium text-gray-900">
                  Reviews ({reviews.length})
                </h3>
                
                {user && !userReview && !showReviewForm && (
                  <button
                    onClick={handleWriteReview}
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    <StarIcon className="-ml-1 mr-2 h-5 w-5" />
                    Write a Review
                  </button>
                )}
              </div>

              {reviewsLoading ? (
                <div className="flex justify-center items-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Review Form */}
                  {showReviewForm && user && (
                    <ReviewForm
                      bookId={book.id}
                      userId={user.id}
                      onReviewSubmitted={handleReviewUpdated}
                      onCancel={handleCancelReview}
                    />
                  )}

                  {/* Review List */}
                  {reviews.length > 0 ? (
                    <ReviewList
                      reviews={reviews}
                      currentUserId={user?.id || null}
                      bookId={book.id}
                      onReviewUpdated={handleReviewUpdated}
                    />
                  ) : !showReviewForm && (
                    <div className="text-center py-8 bg-gray-50 rounded-lg">
                      <StarIcon className="mx-auto h-12 w-12 text-gray-400" />
                      <h4 className="mt-2 text-sm font-medium text-gray-900">No reviews yet</h4>
                      <p className="mt-1 text-sm text-gray-500">
                        Be the first to review this book.
                      </p>
                    </div>
                  )}

                  {/* User's existing review notice */}
                  {user && userReview && !showReviewForm && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="text-sm text-blue-800">
                        You have already reviewed this book. You can edit or delete your review above.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default BookDetail; 